import { identityApi } from '$api-clients/clients';
import type { IdentityRequest } from '$api-clients/hooks';

export type TeamListItem = IdentityRequest<'/teams'>['Data']['data'][number];

const TEAMS_PAGE_LIMIT = 1000;

const idFilter = (ids: string[]) => `id[in]=[${ids.join(',')}]`;
const parentFilter = (ids: string[]) => `parentTeamId[in]=[${ids.join(',')}]`;

/** Fetches every page of a `/teams` query expressed in the identity filter DSL. */
async function fetchTeamsByQuery(q: string): Promise<TeamListItem[]> {
  const teams: TeamListItem[] = [];
  let after: string | undefined;
  let hasNextPage = true;

  while (hasNextPage) {
    // biome-ignore lint/performance/noAwaitInLoops: pagination is inherently sequential — each page needs the previous page's cursor
    const { data, error } = await identityApi.GET('/teams', {
      params: { query: { q, limit: TEAMS_PAGE_LIMIT, after } },
    });

    if (error !== undefined) {
      throw new Error(error.message ?? 'Could not fetch teams');
    }
    if (!data) {
      throw new Error('Could not fetch teams');
    }

    teams.push(...data.data);

    if (data.paging?.next && data.paging.cursors?.after) {
      after = data.paging.cursors.after;
    } else {
      hasNextPage = false;
    }
  }

  return teams;
}

/**
 * The two reads the org walk needs, kept behind an interface so the traversal
 * logic can be exercised against an in-memory graph in tests. Both reads are
 * batched (id list in, team list out) so a whole level resolves in one query.
 */
export type TeamGraphSource = {
  /** Teams by id, in a single query (missing ids are simply absent). */
  getTeams: (ids: string[]) => Promise<TeamListItem[]>;
  /** The direct children of the given teams. */
  getChildren: (parentIds: string[]) => Promise<TeamListItem[]>;
};

/** The production source — talks to identity via the filter DSL + pagination. */
export const identitySource: TeamGraphSource = {
  getTeams: (ids) =>
    ids.length ? fetchTeamsByQuery(idFilter(ids)) : Promise.resolve([]),
  getChildren: (parentIds) =>
    parentIds.length
      ? fetchTeamsByQuery(parentFilter(parentIds))
      : Promise.resolve([]),
};

/** Walks up the `parentTeamId` chain to the organization's root team. */
async function findRoot(
  teamId: string,
  source: TeamGraphSource,
  visited: Set<string> = new Set(),
): Promise<TeamListItem | null> {
  const [team] = await source.getTeams([teamId]);
  if (!team?.parentTeamId || visited.has(team.parentTeamId)) {
    return team ?? null;
  }
  visited.add(team.id);
  return findRoot(team.parentTeamId, source, visited);
}

/**
 * Breadth-first collects every team under `root`, one `getChildren` call per
 * depth level. Already-seen teams are skipped, so cycles terminate.
 */
async function descendFrom(
  root: TeamListItem,
  source: TeamGraphSource,
): Promise<TeamListItem[]> {
  const byId = new Map<string, TeamListItem>([[root.id, root]]);
  let frontier = [root.id];

  while (frontier.length) {
    // biome-ignore lint/performance/noAwaitInLoops: BFS is inherently sequential — each level's query needs the previous level's team ids
    const children = await source.getChildren(frontier);
    const newChildren = children.filter((child) => !byId.has(child.id));
    if (!newChildren.length) {
      break;
    }
    newChildren.forEach((child) => {
      byId.set(child.id, child);
    });
    frontier = newChildren.map((child) => child.id);
  }

  return [...byId.values()];
}

/**
 * Resolves the full set of teams in a team's organization: walk up to the root,
 * then breadth-first down. The `/teams` list also returns the mailing address
 * and logos, so no per-team detail call is needed.
 *
 * One walk per call. Use {@link resolveOrganizations} when resolving many teams
 * that may share an org (e.g. a name search) so every level batches into one
 * query and shared orgs are walked once.
 */
export async function fetchOrganization(
  teamId: string,
  source: TeamGraphSource = identitySource,
): Promise<TeamListItem[]> {
  const root = await findRoot(teamId, source);
  if (!root) {
    return [];
  }
  return descendFrom(root, source);
}

/**
 * Buckets every resolved team into its organization (root + all descendants)
 * and keys each team id to its org's shared team-list. Built from a children
 * index so each org's BFS is cycle-safe and walked once.
 */
function groupOrgsByTeamId(
  teamById: Map<string, TeamListItem>,
): Map<string, TeamListItem[]> {
  const childrenByParentId = new Map<string, TeamListItem[]>();
  for (const team of teamById.values()) {
    if (team.parentTeamId) {
      const siblings = childrenByParentId.get(team.parentTeamId) ?? [];
      siblings.push(team);
      childrenByParentId.set(team.parentTeamId, siblings);
    }
  }

  const orgByTeamId = new Map<string, TeamListItem[]>();
  const roots = [...teamById.values()].filter((team) => !team.parentTeamId);

  for (const root of roots) {
    const orgTeams: TeamListItem[] = [];
    const seen = new Set<string>();
    let frontier = [root];

    while (frontier.length) {
      const unseen = frontier.filter((team) => !seen.has(team.id));
      unseen.forEach((team) => {
        seen.add(team.id);
        orgTeams.push(team);
      });
      frontier = unseen.flatMap(
        (team) => childrenByParentId.get(team.id) ?? [],
      );
    }

    orgTeams.forEach((team) => {
      orgByTeamId.set(team.id, orgTeams);
    });
  }

  return orgByTeamId;
}

/**
 * Resolves the organizations of many seed teams in a fixed number of
 * round-trips: one batched `id[in]` query per ancestor level up to the roots,
 * then one batched `parentTeamId[in]` query per descendant level back down — so
 * request count scales with org depth, not with the number of seeds.
 *
 * Returns every resolved team (seed, ancestor, or descendant) keyed to the full
 * team-list of its organization, so a seed and its (possibly unsearched) root
 * both resolve the same shared list. Unresolvable teams are absent from the map.
 */
export async function resolveOrganizations(
  seedIds: string[],
  source: TeamGraphSource = identitySource,
): Promise<Map<string, TeamListItem[]>> {
  const teamById = new Map<string, TeamListItem>();

  // Walk up: fetch each level of ancestors until every chain reaches a root.
  let ancestorIds = [...new Set(seedIds)];
  while (ancestorIds.length) {
    // biome-ignore lint/performance/noAwaitInLoops: levels are sequential — each query needs the previous level's parent ids
    const teams = await source.getTeams(ancestorIds);
    teams.forEach((team) => {
      teamById.set(team.id, team);
    });
    ancestorIds = [
      ...new Set(
        teams
          .map((team) => team.parentTeamId)
          .filter(
            (parentId): parentId is string =>
              !!parentId && !teamById.has(parentId),
          ),
      ),
    ];
  }

  // Walk down: descend from every discovered root at once, one level per query.
  // `expandedIds` (not `teamById`) guards re-expansion so ancestors fetched on
  // the way up still have their subtrees walked, and cycles terminate.
  const expandedIds = new Set<string>();
  let parentIds = [...teamById.values()]
    .filter((team) => !team.parentTeamId)
    .map((root) => root.id);

  while (parentIds.length) {
    const toExpand = parentIds.filter((id) => !expandedIds.has(id));
    if (!toExpand.length) {
      break;
    }
    toExpand.forEach((id) => {
      expandedIds.add(id);
    });
    // biome-ignore lint/performance/noAwaitInLoops: levels are sequential — each query needs the previous level's team ids
    const children = await source.getChildren(toExpand);
    children.forEach((child) => {
      teamById.set(child.id, child);
    });
    parentIds = children.map((child) => child.id);
  }

  return groupOrgsByTeamId(teamById);
}
